import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Grid, Typography, Divider } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { CompanyClusterConversionCompanyDto, CompanyDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import ClusterConversionCompanyAddEditDialog from './ClusterConversionCompanyAddEditDialog'
import { useAuth } from 'context'

interface ClusterConversionCompanyAddEditDialogProps {
  company: CompanyDto
}

export default function ClusterConversionCompanyListCard({ company }: ClusterConversionCompanyAddEditDialogProps) {
  const { permissions } = useAuth()

  const [isClusterConversionCompanySaving, setIsClusterConversionCompanySaving] = useState<boolean>(false)
  const [clusterConversionCompanyAddEditDialogOpen, setClusterConversionCompanyAddEditDialogOpen] = useState<boolean>(false)
  const [companyClusterConversionCompany, setCompanyClusterConversionCompany] = useState<CompanyClusterConversionCompanyDto>(
    new CompanyClusterConversionCompanyDto()
  )
  const [isGettingClusterConversionCompany, setIsGettingClusterConversionCompany] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingClusterConversionCompany(true)
      controllers
        .getCompanyClusterConversionInformation(company.companyGuid)
        .then(response => {
          setCompanyClusterConversionCompany(response.value)
        })
        .finally(() => {
          setIsGettingClusterConversionCompany(false)
        })
    }
  }, [company])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <ClusterConversionCompanyAddEditDialog
          initialValues={companyClusterConversionCompany}
          loading={isClusterConversionCompanySaving}
          onClose={() => {
            setClusterConversionCompanyAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsClusterConversionCompanySaving(true)
            controllers
              .updateCompanyClusterConversionCompanyInformation(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Cluster Conversion Company Information Saved Successfully!', { variant: 'success' })
                setCompanyClusterConversionCompany(response.value)
                setClusterConversionCompanyAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsClusterConversionCompanySaving(false)
              })
          }}
          open={clusterConversionCompanyAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Cluster Conversion Company
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setIsClusterConversionCompanySaving(false)
                      setClusterConversionCompanyAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingClusterConversionCompany && (
              <Grid
                item
                container
                alignItems='center'
                justifyContent='center'
                sx={{
                  my: 1
                }}
              >
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingClusterConversionCompany && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Cluster Conversion Company Code'
                    value={companyClusterConversionCompany.clusterConversionCompanyCode}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||This card only shows if the company is marked as a Cluster Conversion Company. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
